import { useState, useCallback, useEffect, useRef } from 'react';
import type { RequestLog, PaginatedResponse } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';
import { useWebSocket, type ConnectionStatus } from './useWebSocket';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Hard stop on how many rows we hold. RequestItem is unvirtualized markup inside a
 * 600px scroller, so past a couple of thousand rows the list gets sluggish. Users
 * who need more than this have the JSON/CSV export.
 */
const MAX_LOADED = 2000;

/**
 * A page can come back entirely made of rows we already hold: the query is
 * `ORDER BY timestamp DESC LIMIT ? OFFSET ?`, so live traffic inserts at the front
 * of the window and shifts our offset backwards. Rather than let the click be a
 * no-op, advance and retry a bounded number of times.
 */
const MAX_DUPLICATE_PAGE_RETRIES = 3;

export interface UseRequestLogsOptions {
  projectId?: string;
  endpointId?: string;
  /** The project's Durable Object name — forwarded to useWebSocket as `subdomain`. */
  doName?: string;
  pageSize?: number;
}

export interface UseRequestLogsReturn {
  requests: RequestLog[];
  status: ConnectionStatus;
  historyLoading: boolean;
  loadingMore: boolean;
  historyError: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  retryHistory: () => Promise<void>;
  clearRequests: () => Promise<void>;
}

/**
 * Union `incoming` into `existing` by id, newest first.
 *
 * Timestamps are ISO-8601 UTC, so a lexicographic compare is also a chronological
 * one; id is the tiebreaker for rows logged in the same millisecond.
 *
 * Contract: returns `existing` **by reference** when nothing new was added. The
 * merge effect below feeds its own output back in, so a fresh array on a no-op
 * would loop forever.
 */
export function mergeLogs(incoming: RequestLog[], existing: RequestLog[]): RequestLog[] {
  if (incoming.length === 0) return existing;

  const seen = new Set(existing.map(req => req.id));
  const added = incoming.filter(req => !seen.has(req.id));
  if (added.length === 0) return existing;

  return [...existing, ...added].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp) || b.id.localeCompare(a.id)
  );
}

export function useRequestLogs(options: UseRequestLogsOptions = {}): UseRequestLogsReturn {
  const { projectId, endpointId, doName, pageSize = DEFAULT_PAGE_SIZE } = options;

  // The live stream. useWebSocket also seeds its own 100-row history on connect —
  // we keep that, because on *reconnect* it is the only thing that fills the gap
  // for requests that landed while the socket was down. Both paths return the same
  // newest-N rows, so the id dedup in mergeLogs makes the overlap a no-op.
  const { status, requests: liveRequests, clearRequests: clearLive } = useWebSocket({
    subdomain: doName,
    endpointId,
    projectId,
  });

  const [requests, setRequests] = useState<RequestLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  /** Rows the server has handed us across REST pages; doubles as the next offset. */
  const restRowsRef = useRef(0);

  /**
   * Mirror of `requests`, so a merge can report whether it changed anything
   * without depending on when React chooses to run a setState updater.
   */
  const rowsRef = useRef<RequestLog[]>([]);

  /** Merges `incoming` into the list. Returns true if it added anything. */
  const applyRows = useCallback((incoming: RequestLog[]): boolean => {
    const merged = mergeLogs(incoming, rowsRef.current);
    if (merged === rowsRef.current) return false;
    rowsRef.current = merged;
    setRequests(merged);
    return true;
  }, []);

  const resetRows = useCallback(() => {
    rowsRef.current = [];
    setRequests([]);
    restRowsRef.current = 0;
    setHasMore(false);
  }, []);

  const fetchPage = useCallback(async (offset: number): Promise<RequestLog[]> => {
    if (!projectId) return [];

    const params = new URLSearchParams({ limit: String(pageSize), offset: String(offset) });
    if (endpointId) params.set('endpointId', endpointId);

    const json = await api.get<PaginatedResponse<RequestLog>>(
      `/api/projects/${projectId}/logs?${params.toString()}`
    );
    const page = json.data || [];

    restRowsRef.current = offset + page.length;
    // Page length, not pagination.total: total is a live COUNT(*) that grows with
    // incoming traffic, so comparing against it would report hasMore forever on a
    // busy endpoint. A short page is an unambiguous end-of-history signal.
    setHasMore(page.length === pageSize && restRowsRef.current < MAX_LOADED);

    return page;
  }, [projectId, endpointId, pageSize]);

  const loadHistory = useCallback(async () => {
    if (!projectId) return;

    setHistoryLoading(true);
    setHistoryError(null);
    try {
      applyRows(await fetchPage(0));
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : 'Failed to load request history');
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, fetchPage, applyRows]);

  const loadMore = useCallback(async () => {
    if (!projectId || loadingMore) return;

    setLoadingMore(true);
    setHistoryError(null);
    try {
      for (let attempt = 0; attempt < MAX_DUPLICATE_PAGE_RETRIES; attempt++) {
        const page = await fetchPage(restRowsRef.current);
        const added = applyRows(page);

        // Stop as soon as the page moved us forward, or the server says that's all.
        if (added || page.length < pageSize) break;
      }
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : 'Failed to load older requests');
    } finally {
      setLoadingMore(false);
    }
  }, [projectId, loadingMore, fetchPage, pageSize, applyRows]);

  // Seed page 0, and start over when the endpoint changes.
  useEffect(() => {
    resetRows();
    loadHistory();
  }, [resetRows, loadHistory]);

  // Fold the live stream into the merged list. applyRows is a no-op when every
  // incoming row is already held, which is what keeps this from re-running itself.
  useEffect(() => {
    applyRows(liveRequests);
  }, [liveRequests, applyRows]);

  const clearRequests = useCallback(async () => {
    // Await the DELETE and the live buffer reset first: if we empty our own list
    // while liveRequests is still populated, the merge effect repopulates it on
    // the next render.
    // A failed DELETE leaves the list as-is so it keeps matching the server.
    if (!(await clearLive())) return;
    resetRows();
    setHistoryError(null);
  }, [clearLive, resetRows]);

  return {
    requests,
    status,
    historyLoading,
    loadingMore,
    historyError,
    hasMore,
    loadMore,
    retryHistory: loadHistory,
    clearRequests,
  };
}
